import { useEffect } from 'react'

/**
 * AB#2040: refuse drag-and-drop of text into and out of form fields.
 *
 * Browsers let a highlighted run inside an `<input>` be dragged out and
 * dropped into any other editable field. A click that slides a few pixels on
 * the way to the next field is enough to do it, and the destination is
 * overwritten with no keystroke, no validation prompt and nothing in the UI to
 * say it happened — so a broker who doesn't catch it as it happens saves the
 * wrong number. QA reproduced it across every field pairing, including into
 * dropdowns (whose `<input>` is a real text field underneath), and AB#1985's
 * "Equity0.00" Description was the same mechanism reaching production.
 *
 * Nothing in the app needs text dragging, so both directions are refused:
 *
 *   - `dragstart` on a field — the selection can never become drag data, so
 *     field-to-field dragging is impossible at the source.
 *   - `dragenter` / `dragover` / `drop` on a field — text dragged from
 *     anywhere else (page copy, another tab, another application) can't land
 *     in one either.
 *
 * Copy/paste is untouched: this only removes the pointer gesture, not the
 * clipboard.
 *
 * Listeners sit on `document` in the capture phase rather than on a form
 * root, because "all fields" includes the ones in modals, which Mantine
 * renders in a portal outside the form.
 *
 * Deliberately NOT guarded:
 *   - File drags (`dataTransfer` carrying `Files`) pass through untouched, so
 *     the FileUpload / Dropzone drop targets keep working even when the
 *     pointer happens to be over a field inside them.
 *   - `contenteditable` — the rich-text editor's own drag handling is a
 *     feature, and it isn't a form field.
 */

// Input types that hold user-entered text. Listing them explicitly (rather
// than excluding buttons and checkboxes) is what keeps `type="file"` — the
// Dropzone / FileUpload browse input — outside the guard.
const TEXT_ENTRY_TYPES = new Set([
    'text',
    'search',
    'tel',
    'url',
    'email',
    'password',
    'number',
    'date',
    'datetime-local',
    'month',
    'week',
    'time',
])

function isTextEntryField(target: EventTarget | null): boolean {
    if (target instanceof HTMLTextAreaElement) return true
    // `.type` is normalized by the DOM: lowercased, and `text` when the
    // attribute is missing or unrecognized — so a Mantine Select's readonly
    // `<input>` counts, which is what makes "drop onto a dropdown" impossible.
    if (target instanceof HTMLInputElement) return TEXT_ENTRY_TYPES.has(target.type)
    return false
}

function carriesFiles(event: Event): boolean {
    const types = (event as DragEvent).dataTransfer?.types
    return types ? Array.from(types).includes('Files') : false
}

export function useInputDragGuard(): void {
    useEffect(() => {
        const onDragStart = (event: Event) => {
            if (isTextEntryField(event.target)) event.preventDefault()
        }

        // Cancelling `dragenter`/`dragover` is what marks an element as a drop
        // target at all; a `dropEffect` of `none` then refuses the drop and
        // paints the no-drop cursor, so the broker sees the refusal before
        // letting go rather than watching a field change under them.
        const onDragOver = (event: Event) => {
            if (!isTextEntryField(event.target) || carriesFiles(event)) return
            event.preventDefault()
            const dataTransfer = (event as DragEvent).dataTransfer
            if (dataTransfer) dataTransfer.dropEffect = 'none'
        }

        // Belt and braces for a browser that delivers the drop regardless:
        // cancelling it stops the default insertion into the field.
        const onDrop = (event: Event) => {
            if (!isTextEntryField(event.target) || carriesFiles(event)) return
            event.preventDefault()
            event.stopPropagation()
        }

        document.addEventListener('dragstart', onDragStart, true)
        document.addEventListener('dragenter', onDragOver, true)
        document.addEventListener('dragover', onDragOver, true)
        document.addEventListener('drop', onDrop, true)
        return () => {
            document.removeEventListener('dragstart', onDragStart, true)
            document.removeEventListener('dragenter', onDragOver, true)
            document.removeEventListener('dragover', onDragOver, true)
            document.removeEventListener('drop', onDrop, true)
        }
    }, [])
}
